import type { ActionsUnion} from '@rxsv/core';
import { U, createReducer, fromActions } from '@rxsv/core';
import { of } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';

import { initEmailData } from 'portlet/models';
import type { EmailSupportStore, EmailTemplate, PortletDebugInformation } from 'header/types';
import type { AppError } from 'portlet/common/errors';

import type { AppSelector } from '../selectors';
import type { EffectFactory } from '../effects';

export const initialState: EmailSupportStore = {
    supportEmailData: initEmailData,
    loading: false,
    error: null,
};

export const Action = U.createUnion(
    U.caseOf('RESET_EMAIL_DATA')(),
    U.caseOf('SET_EMAIL_DATA')<EmailTemplate>(),
    U.caseOf('SEND_MAIL_REQUESTED')<{ email: EmailTemplate; debugInfo: PortletDebugInformation }>(),
    U.caseOf('SEND_MAIL_SUCCEDED')(),
    U.caseOf('SEND_MAIL_FAILED')<AppError>(),
);

export type Action = ActionsUnion<typeof Action>;
export const ErrorActions = [Action.SEND_MAIL_FAILED];

export const reducer = createReducer(initialState)<Action>({
    RESET_EMAIL_DATA: () => initialState,
    SET_EMAIL_DATA: (state, { payload }) => ({
        ...state,
        supportEmailData: payload,
    }),
    SEND_MAIL_REQUESTED: state => ({
        ...state,
        loading: true,
    }),
    SEND_MAIL_SUCCEDED: state => ({
        ...state,
        loading: false,
        error: null,
    }),
    SEND_MAIL_FAILED: (state, { payload }) => ({
        ...state,
        loading: false,
        error: payload,
    }),
});

const supportEmailStateSelector: AppSelector<EmailSupportStore> = state => {
    return state.emailSupport;
};

export const Selectors = {
    supportEmailStateSelector,
};

export const effect: EffectFactory =
    ({ mailingService }) =>
    action$ => {
        return action$.pipe(
            fromActions(Action.SEND_MAIL_REQUESTED),
            map(({ payload }) => payload),
            switchMap(({ email, debugInfo }) => {
                return mailingService.sendMail(email, debugInfo).pipe(
                    map(Action.SEND_MAIL_SUCCEDED),
                    catchError((error: AppError) => {
                        return of(Action.SEND_MAIL_FAILED(error));
                    }),
                );
            }),
        );
    };
